/**
 * Get authentication token from cookies or localStorage
 * Checks both storage methods for compatibility
 */
export function getAuthToken(): string | null {
  // Email/password auth writes the freshest token to localStorage. Prefer it
  // so an older OAuth cookie cannot silently authenticate API calls as a
  // different (or expired) session.
  if (typeof window !== 'undefined' && window.localStorage) {
    const localToken = localStorage.getItem('token');
    if (localToken) return localToken;
  }

  // OAuth flows also persist a cookie; use it when no local token exists.
  if (typeof document !== 'undefined') {
    const cookies = document.cookie.split(';');
    const tokenCookie = cookies.find(cookie => cookie.trim().startsWith('token='));

    if (tokenCookie) {
      const token = tokenCookie.split('=')[1];
      return token || null;
    }
  }

  return null;
}

/**
 * Set authentication token in both cookies and localStorage
 * for maximum compatibility
 */
export function setAuthToken(token: string): void {
  // Set in cookies (7 days expiry)
  if (typeof document !== 'undefined') {
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + 7);
    document.cookie = `token=${token}; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax`;
  }

  // Also set in localStorage for backward compatibility
  if (typeof window !== 'undefined' && window.localStorage) {
    localStorage.setItem('token', token);
  }
}

/**
 * Remove authentication token from both cookies and localStorage
 */
export function removeAuthToken(): void {
  // Remove from cookies
  if (typeof document !== 'undefined') {
    document.cookie = 'token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  }

  // Remove from localStorage
  if (typeof window !== 'undefined' && window.localStorage) {
    localStorage.removeItem('token');
  }
}
